import { endOfDay, parseISO } from "date-fns";
import { memo, useCallback } from "react";
import ExpandableCard from "@/components/common/cards/expandableCard/expandableCard";
import { CardChipProps } from "@/components/common/chips/cardChip";
import CardThumbnail from "@/components/common/thumbnail/cardThumbnail";
import { VIOLET, YELLOW } from "@/config/mui/colorPalette";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import usePersistedStore from "@/store/usePersistedStore";
import useOnlineStorePackagesStore from "@/store/views/onlineStore/onlineStorePackagesStore";
import { getTimeDifferenceString } from "@/utils/date";
import { MedspaPackage } from "@/views/online-store/onlineStoreView";
import PackageCardContent from "./oldPackageCardContent";
import PackageCardStoreButton from "./oldPackageCardStoreButton";

type OldPackageCardProps = {
  packageItem: MedspaPackage;
  isFeaturedDeal?: boolean;
  customizationEnabled: boolean;
  customizationPrimaryColor: string;
  medspaId: string;
};

const CARD_HEIGHT = 368;

const MemoizedPackageCardStoreButton = memo(PackageCardStoreButton);

/**
 * @deprecated Use PackageCard component instead
 * https://linear.app/moxie/issue/DEV-5391/remove-old-package-card-component
 */
export default function OldPackageCard({
  packageItem,
  isFeaturedDeal,
  customizationEnabled,
  customizationPrimaryColor,
  medspaId,
}: OldPackageCardProps) {
  const store = useOnlineStorePackagesStore(medspaId);

  // For some reason while using persist middleware usePersistedStore `(state) => state.addPackage` returns undefined
  // Can't get methods using hook, we have to get them using .getState()
  const { changePackageQuantity, getPackageQuantity, setPackageQuantity } =
    store.getState();

  const packagesQuantity = usePersistedStore(
    store,
    (state) => state.packagesQuantity
  );

  const packageQuantity = getPackageQuantity(packageItem.id);

  const onAmountChange = (change: 1 | -1) => {
    changePackageQuantity(packageItem.id, change);
  };

  const onInputChange = (value: string) => {
    setPackageQuantity(packageItem.id, +value);
  };

  const isMobile = useMoxieMediaQuery("ipad-ver", "down");

  const {
    endDate,
    packageName,
    packagePrice,
    packageItems,
    packageSavings,
    description,
    additionalInfo,
  } = packageItem;

  const thumbnailUrl = additionalInfo?.thumbnail ?? "/assets/images/gift.png";

  const thumbnailChips: CardChipProps[] = [
    +packageSavings > 0 && {
      label: `$${(+packageSavings).toFixed(0)} Savings`,
      textColor: VIOLET[100],
      backgroundColor: VIOLET[40],
    },
    isFeaturedDeal && {
      label: `Ends in ${getTimeDifferenceString(endOfDay(parseISO(endDate)), new Date())}`,
      textColor: YELLOW[100],
      backgroundColor: YELLOW[40],
    },
  ].filter(Boolean);

  const memoizedThumbnail = useCallback(
    (thumbnailHeight: number) => (
      <CardThumbnail
        url={thumbnailUrl}
        height={thumbnailHeight}
        chips={thumbnailChips}
        isMobile={isMobile}
      />
    ),
    [thumbnailUrl, thumbnailChips, isMobile]
  );

  const memoizedContent = useCallback(
    (isFocused: boolean) => (
      <PackageCardContent
        isFocused={isFocused}
        packageName={packageName}
        packagePrice={+packagePrice}
        description={description}
        packageItems={packageItems}
        isMobile={isMobile}
      />
    ),
    [packageName, packagePrice, description, packageItems, isMobile]
  );

  // Required to avoid Next.js `Text content does not match server-rendered HTML` error when using persist middleware
  if (!packagesQuantity) return <></>;

  return (
    <ExpandableCard
      height={CARD_HEIGHT}
      isMobile={isMobile}
      isFeaturedDeal={isFeaturedDeal}
      thumbnail={memoizedThumbnail}
      content={memoizedContent}
      button={() => (
        <MemoizedPackageCardStoreButton
          packageQuantity={packageQuantity}
          onAmountChange={onAmountChange}
          onInputChange={onInputChange}
          unitPrice={+packagePrice}
          isMobile={isMobile}
          customizationEnabled={customizationEnabled}
          customizationPrimaryColor={customizationPrimaryColor}
          type="card"
        />
      )}
    />
  );
}
